import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Clock, Thermometer, Sparkles, FileText, ShieldCheck, Users,
  ChefHat, ClipboardCheck, Bell, BarChart3, Zap, Lock,
  ChevronRight, Check, Menu, X, ArrowRight
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1767785990437-dfe1fe516fe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwa2l0Y2hlbiUyMHByb2Zlc3Npb25hbCUyMGNoZWYlMjB0ZWFtfGVufDB8fHx8MTc3NDM2OTIyNXww&ixlib=rb-4.1.0&q=85&w=1200";
const TABLET_IMG = "https://images.unsplash.com/photo-1728044849280-10a1a75cff83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwY29tcGxpYW5jZSUyMGZvb2QlMjBzYWZldHklMjBpbnNwZWN0aW9uJTIwdGFibGV0JTIwZGlnaXRhbHxlbnwwfHx8fDE3NzQzNjkyMzJ8MA&ixlib=rb-4.1.0&q=85&w=800";

const FEATURES = [
  { icon: Clock, title: "Control Horari", desc: "Fitxatge digital amb NFC, kiosk i app. Compliment RD-Llei 8/2019 garantit.", color: "bg-blue-500" },
  { icon: Thermometer, title: "Temperatures APPCC", desc: "Registre automatitzat de temperatures amb alertes per desviacions.", color: "bg-red-500" },
  { icon: ClipboardCheck, title: "Neteja i Desinfecció", desc: "Checklists diaris per zones amb verificació fotogràfica.", color: "bg-green-500" },
  { icon: ChefHat, title: "Fitxes Tècniques", desc: "Gestió d'al·lèrgens, receptes i fitxes amb extracció IA de PDFs.", color: "bg-amber-500" },
  { icon: FileText, title: "Documents", desc: "Arxiu centralitzat amb alertes de caducitat i renovació.", color: "bg-purple-500" },
  { icon: ShieldCheck, title: "Compliment Normatiu", desc: "APPCC, RGPD, PRL i protocol d'assetjament en una sola plataforma.", color: "bg-teal-500" },
  { icon: Bell, title: "Alertes Intel·ligents", desc: "Notificacions proactives per documents, temperatures i incidències.", color: "bg-orange-500" },
  { icon: Users, title: "Gestió d'Equip", desc: "Contractes, hores extres, torns i control de personal.", color: "bg-indigo-500" },
  { icon: Sparkles, title: "Assistent IA", desc: "Intel·ligència artificial per generar fitxes tècniques i analitzar documents.", color: "bg-pink-500" },
];

const PLANS = [
  {
    name: "Bàsic",
    price: "29",
    desc: "Per establiments petits",
    features: ["Control horari", "Temperatures", "Neteja", "Fins a 10 treballadors", "Suport per email"],
    highlighted: false,
  },
  {
    name: "Professional",
    price: "59",
    desc: "Per restaurants i bars",
    features: ["Tot del Bàsic", "Fitxes tècniques + IA", "Documents", "Alertes", "Fins a 30 treballadors", "APPCC complet", "Suport prioritari"],
    highlighted: true,
  },
  {
    name: "Premium",
    price: "99",
    desc: "Per cadenes i grups",
    features: ["Tot del Professional", "Multi-local", "RGPD + PRL", "Analítica avançada", "Treballadors il·limitats", "API i integracions", "Gestor de compte dedicat"],
    highlighted: false,
  },
];

const STEPS = [
  { num: "01", title: "Registra't", desc: "Crea el teu compte en 2 minuts i configura el teu establiment." },
  { num: "02", title: "Configura", desc: "Afegeix el teu equip, zones de neteja, equips de temperatura i documents." },
  { num: "03", title: "Compleix", desc: "Deixa que Ho-SYS gestioni la normativa mentre tu et centres en el teu negoci." },
];

function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Ho-SYS" className="h-9 w-9" />
          <span className="text-xl font-bold tracking-tight text-[#7B2D3F]">Ho-SYS</span>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
          <a href="#funcionalitats" className="hover:text-[#7B2D3F] transition-colors">Funcionalitats</a>
          <a href="#com-funciona" className="hover:text-[#7B2D3F] transition-colors">Com funciona</a>
          <a href="#preus" className="hover:text-[#7B2D3F] transition-colors">Preus</a>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="text-sm font-medium">Iniciar sessió</Button>
          </Link>
          <Link to="/register">
            <Button className="bg-[#7B2D3F] hover:bg-[#5a1f2e] text-sm font-medium px-5">
              Prova gratuïta
            </Button>
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} className="md:hidden p-2">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t px-6 py-4 space-y-3 animate-in slide-in-from-top">
          <a href="#funcionalitats" className="block py-2 text-sm text-gray-700" onClick={() => setOpen(false)}>Funcionalitats</a>
          <a href="#com-funciona" className="block py-2 text-sm text-gray-700" onClick={() => setOpen(false)}>Com funciona</a>
          <a href="#preus" className="block py-2 text-sm text-gray-700" onClick={() => setOpen(false)}>Preus</a>
          <hr />
          <Link to="/login" className="block py-2 text-sm text-gray-700">Iniciar sessió</Link>
          <Link to="/register">
            <Button className="w-full bg-[#7B2D3F] hover:bg-[#5a1f2e]">Prova gratuïta</Button>
          </Link>
        </div>
      )}
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#7B2D3F]/5 via-white to-amber-50/40" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#7B2D3F]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-amber-100/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7B2D3F]/8 rounded-full border border-[#7B2D3F]/15">
              <Zap size={14} className="text-[#7B2D3F]" />
              <span className="text-xs font-semibold text-[#7B2D3F] uppercase tracking-wider">Nou: Extracció IA de fitxes tècniques</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.1] tracking-tight">
              Gestió normativa
              <span className="block text-[#7B2D3F]">sense complicacions</span>
            </h1>

            <p className="text-lg text-gray-600 max-w-lg leading-relaxed">
              La plataforma tot-en-un per a restaurants, bars i establiments de restauració.
              APPCC, control horari, temperatures, neteja i molt més — tot automatitzat.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#7B2D3F] hover:bg-[#5a1f2e] text-base px-8 h-12 w-full sm:w-auto group">
                  Comença gratis
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#funcionalitats">
                <Button size="lg" variant="outline" className="text-base px-8 h-12 w-full sm:w-auto border-gray-300 hover:border-[#7B2D3F] hover:text-[#7B2D3F]">
                  Veure funcionalitats
                </Button>
              </a>
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5"><Check size={16} className="text-green-500" /> 14 dies gratis</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-green-500" /> Sense targeta</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-green-500" /> Cancel·la quan vulguis</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#7B2D3F]/10 to-amber-100/30 rounded-2xl blur-2xl" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/60">
              <img
                src={HERO_IMG}
                alt="Cuina professional"
                className="w-full h-auto object-cover aspect-[4/3]"
                loading="eager"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-500/90 flex items-center justify-center">
                    <ShieldCheck size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">100% en compliment</p>
                    <p className="text-white/70 text-xs">APPCC · RGPD · PRL · RD-Llei 8/2019</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="bg-[#7B2D3F] py-10">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
        {[
          ["500+", "Establiments"],
          ["15.000+", "Treballadors gestionats"],
          ["99.8%", "Uptime garantit"],
          ["< 2 min", "Temps de configuració"],
        ].map(([num, label]) => (
          <div key={label}>
            <p className="text-3xl md:text-4xl font-bold">{num}</p>
            <p className="text-sm text-white/70 mt-1">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section id="funcionalitats" className="py-20 md:py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Tot el que necessites, en una sola plataforma
          </h2>
          <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
            Substitueix fulls d'Excel, papers i carpetes per una solució digital completa.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group bg-white rounded-xl p-6 border border-gray-100 hover:border-[#7B2D3F]/20 hover:shadow-lg transition-all duration-300"
            >
              <div className={`w-11 h-11 rounded-lg ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <f.icon size={22} className="text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 text-base mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="com-funciona" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-12">
              Comença en 3 passos
            </h2>
            <div className="space-y-10">
              {STEPS.map((s) => (
                <div key={s.num} className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#7B2D3F] flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{s.num}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">{s.title}</h3>
                    <p className="text-gray-500 mt-1">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-bl from-[#7B2D3F]/5 to-amber-50/30 rounded-2xl blur-xl" />
            <img
              src={TABLET_IMG}
              alt="Ho-SYS en tablet"
              className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section id="preus" className="py-20 md:py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Preus transparents
          </h2>
          <p className="mt-4 text-lg text-gray-500">Sense sorpreses. Sense permanència.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl p-8 ${
                p.highlighted
                  ? "bg-[#7B2D3F] text-white ring-4 ring-[#7B2D3F]/20 scale-105"
                  : "bg-white border border-gray-200"
              } transition-all duration-300 hover:shadow-xl`}
            >
              {p.highlighted && (
                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-semibold mb-4 uppercase tracking-wider">
                  Més popular
                </span>
              )}
              <h3 className={`text-xl font-bold ${p.highlighted ? "text-white" : "text-gray-900"}`}>
                {p.name}
              </h3>
              <p className={`text-sm mt-1 ${p.highlighted ? "text-white/70" : "text-gray-500"}`}>
                {p.desc}
              </p>
              <div className="mt-6 mb-8">
                <span className={`text-5xl font-extrabold ${p.highlighted ? "text-white" : "text-gray-900"}`}>
                  {p.price}
                </span>
                <span className={`text-base ml-1 ${p.highlighted ? "text-white/60" : "text-gray-400"}`}>
                  /mes per local
                </span>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2.5">
                    <Check size={16} className={`flex-shrink-0 mt-0.5 ${p.highlighted ? "text-green-300" : "text-green-500"}`} />
                    <span className={`text-sm ${p.highlighted ? "text-white/90" : "text-gray-600"}`}>{feat}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button
                  className={`w-full h-11 font-semibold ${
                    p.highlighted
                      ? "bg-white text-[#7B2D3F] hover:bg-gray-100"
                      : "bg-[#7B2D3F] hover:bg-[#5a1f2e] text-white"
                  }`}
                >
                  Comença ara
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="bg-gradient-to-br from-[#7B2D3F] to-[#5a1f2e] rounded-3xl p-12 md:p-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <Lock size={40} className="mx-auto mb-6 text-white/80" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Preparat per passar la inspecció?
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto mb-8">
              Amb Ho-SYS, tindràs tota la documentació al dia i a un clic de distància.
              Prova-ho gratis durant 14 dies.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-[#7B2D3F] hover:bg-gray-100 text-base px-10 h-12 font-semibold">
                Comença la prova gratuïta
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-400 py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/logo.png" alt="Ho-SYS" className="h-8 w-8 brightness-200" />
              <span className="text-lg font-bold text-white">Ho-SYS</span>
            </div>
            <p className="text-sm leading-relaxed">
              Plataforma de gestió normativa per a establiments de restauració.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Producte</h4>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#funcionalitats" className="hover:text-white transition-colors">Funcionalitats</a></li>
              <li><a href="#preus" className="hover:text-white transition-colors">Preus</a></li>
              <li><a href="#com-funciona" className="hover:text-white transition-colors">Com funciona</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Normativa</h4>
            <ul className="space-y-2.5 text-sm">
              <li><span>APPCC</span></li>
              <li><span>RGPD</span></li>
              <li><span>PRL</span></li>
              <li><span>RD-Llei 8/2019</span></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Contacte</h4>
            <ul className="space-y-2.5 text-sm">
              <li><a href="mailto:info@ho-sys.com" className="hover:text-white transition-colors">info@ho-sys.com</a></li>
              <li><span>Reus, Catalunya</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">&copy; {new Date().getFullYear()} Ho-SYS. Tots els drets reservats.</p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="hover:text-white transition-colors">Política de privacitat</a>
            <a href="#" className="hover:text-white transition-colors">Termes d'ús</a>
            <a href="#" className="hover:text-white transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <StatsBar />
      <FeaturesSection />
      <HowItWorks />
      <PricingSection />
      <CTASection />
      <Footer />
    </div>
  );
}
